/**
 * LUMINA · ToolsPage.jsx  v2.0  (Phase 90A)
 * 易学工具箱 — 老黄历 · 解梦 · 求签 · AI起名
 * Phase A: 28星宿+黄道黑道+rigidity / 灵签完整Golden Dataset / 解梦原典+场景变调 / 起名笔画
 */

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../constants/config";

const GD  = "#064E3B";
const GDH = "#D4AF37";
const BG  = "#FDFBF7";
const CARD_BG = "#FFFFFF";

const tabs = [
  { key: "almanac",    label: "📅 老黄历",  desc: "每日宜忌·28星宿·黄道黑道" },
  { key: "dreams",     label: "💭 解梦",     desc: "周公解梦原典·场景变调" },
  { key: "divination", label: "🏮 观音灵签", desc: "100签Golden Dataset·诗意解曰仙机" },
  { key: "naming",     label: "✨ AI起名",   desc: "500+精选字库·康熙笔画标注" },
];

const card = {
  background: CARD_BG,
  border: `1px solid rgba(212,175,55,0.2)`,
  borderRadius: 12,
  padding: 16,
};

// ═══════════════════════════════════════════════════════════════════════════════
// 老黄历 Tab
// ═══════════════════════════════════════════════════════════════════════════════

function AlmanacTab() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    fetch(`${API}/tools/almanac`).then(r => r.json()).then(setData);
    fetch(`${API}/tools/almanac/insight`).then(r => r.json()).then(setInsight);
    const now = new Date();
    fetch(`${API}/tools/almanac/month?year=${now.getFullYear()}&month=${now.getMonth()+1}`)
      .then(r => r.json()).then(setMonth);
  }, []);

  if (!data) return <p style={{color:"#6B7280"}}>加载中...</p>;

  const rl = data.rigidity_level;
  const rColor = rl==="吉"?"#059669":rl==="平"?"#6B7280":rl==="慎"?"#D97706":rl==="凶"?"#DC2626":"#991B1B";
  const rBg = rl==="吉"?"#ECFDF5":rl==="平"?"#F3F4F6":rl==="慎"?"#FEF3C7":rl==="凶"?"#FEF2F2":"#7F1D1D";

  return (
    <div>
      {/* 今日概览 */}
      <div style={{...card, textAlign:"center", marginBottom:16}}>
        <div style={{fontSize:14,color:"#6B7280"}}>{data.gregorian} 周{data.weekday}</div>
        <div style={{fontSize:24,color:GD,fontWeight:700,margin:"6px 0"}}>
          {data.lunar} · {data.ganzhi}日
        </div>
        <div style={{display:"flex",justifyContent:"center",gap:12,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:13,color:"#6B7280"}}>🐲 {data.shengxiao}年</span>
          <span style={{...badge(data.jianchu==="成"||data.jianchu==="开"?"#ECFDF5":data.jianchu==="破"||data.jianchu==="闭"?"#FEF2F2":"#F3F4F6",data.jianchu==="成"||data.jianchu==="开"?"#065F46":data.jianchu==="破"||data.jianchu==="闭"?"#991B1B":"#4B5563")}}>
            {data.jianchu}日
          </span>
          <span style={{fontSize:12,color:"#DC2626",fontWeight:600}}>⚡{data.chong_sha}</span>
        </div>

        {/* 28星宿 + 黄道黑道 */}
        <div style={{display:"flex",justifyContent:"center",gap:10,marginTop:8,flexWrap:"wrap"}}>
          <span style={{...badge(data.star_28?.fortune==="吉"?"#ECFDF5":"#FEF2F2",data.star_28?.fortune==="吉"?"#065F46":"#991B1B")}}>
            ⭐ {data.star_28?.name}宿({data.star_28?.animal}) · {data.star_28?.group}
          </span>
          <span style={{...badge(data.huanghei_dao?.type==="黄道"?"#ECFDF5":"#FEF2F2",data.huanghei_dao?.type==="黄道"?"#065F46":"#991B1B")}}>
            {data.huanghei_dao?.type==="黄道"?"🟡":"⚫"} {data.huanghei_dao?.name}({data.huanghei_dao?.type})
          </span>
        </div>

        {/* Rigidity 评分 */}
        <div style={{marginTop:10,display:"flex",justifyContent:"center",alignItems:"center",gap:8}}>
          <span style={{...badge(rBg,rColor),fontSize:13,fontWeight:700}}>
            {rl==="吉"?"✅":rl==="平"?"➖":rl==="慎"?"⚠️":rl==="凶"?"🚫":"⛔"} 今日{data.rigidity_score}分 · {rl}
          </span>
          {data.special_bad?.map((sb,i) => (
            <span key={i} style={{...badge("#FEF2F2","#DC2626"),fontSize:11}}>{sb.label}</span>
          ))}
        </div>
        {data.rigidity_advice && (
          <p style={{marginTop:8,fontSize:12,color:rColor,background:rBg,padding:"6px 12px",borderRadius:8,lineHeight:1.6}}>
            {data.rigidity_advice}
          </p>
        )}
      </div>

      {/* LLM 每日白话概述 */}
      {insight?.insight && (
        <div style={{...card,marginBottom:16,background:"linear-gradient(135deg,#FFFDF5,#FEFCE8)",border:"1px solid rgba(212,175,55,0.25)"}}>
          <div style={{fontSize:11,color:GDH,fontWeight:600,marginBottom:6}}>💬 今日概述</div>
          <p style={{fontSize:13,color:"#4B5563",lineHeight:1.9,margin:0}}>{insight.insight}</p>
        </div>
      )}

      {/* 宜忌 */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div style={{...card,background:"#ECFDF5"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#065F46",marginBottom:6}}>✅ 宜</div>
          {(data.yi||[]).map((y,i) => (
            <span key={i} style={{display:"inline-block",background:"#D1FAE5",color:"#065F46",padding:"4px 10px",borderRadius:12,fontSize:12,margin:"2px 4px"}}>{y}</span>
          ))}
          {(!data.yi||data.yi.length===0) && <span style={{fontSize:12,color:"#6B7280"}}>—</span>}
        </div>
        <div style={{...card,background:"#FEF2F2"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#991B1B",marginBottom:6}}>⚠️ 忌</div>
          {(data.ji||[]).map((j,i) => (
            <span key={i} style={{display:"inline-block",background:"#FEE2E2",color:"#991B1B",padding:"4px 10px",borderRadius:12,fontSize:12,margin:"2px 4px"}}>{j}</span>
          ))}
          {(!data.ji||data.ji.length===0) && <span style={{fontSize:12,color:"#6B7280"}}>—</span>}
        </div>
      </div>

      {/* 吉神凶神 */}
      <div style={{...card,marginBottom:16,display:"flex",gap:20,flexWrap:"wrap"}}>
        {data.ji_shen?.length > 0 && (
          <div>
            <span style={{fontSize:11,color:"#9CA3AF"}}>吉神: </span>
            {data.ji_shen.map((s,i) => <span key={i} style={{...badge("#ECFDF5","#065F46"),fontSize:11,marginRight:4}}>{s}</span>)}
          </div>
        )}
        {data.xiong_shen?.length > 0 && (
          <div>
            <span style={{fontSize:11,color:"#9CA3AF"}}>凶神: </span>
            {data.xiong_shen.map((s,i) => <span key={i} style={{...badge("#FEF2F2","#991B1B"),fontSize:11,marginRight:4}}>{s}</span>)}
          </div>
        )}
      </div>

      {/* 彭祖百忌 */}
      {data.pengzu?.length > 0 && (
        <div style={{...card,marginBottom:16,background:"#FFFDF5"}}>
          <div style={{fontSize:12,color:"#6B7280",marginBottom:4}}>📜 彭祖百忌</div>
          {data.pengzu.map((p,i) => <p key={i} style={{fontSize:12,color:"#4B5563",margin:"2px 0"}}>{p}</p>)}
        </div>
      )}

      {/* 节气 */}
      <div style={{...card,marginBottom:16,textAlign:"center"}}>
        <span style={{fontSize:13,color:GD}}>🌿 当前节气: {data.solar_term}</span>
        <span style={{fontSize:12,color:"#6B7280",marginLeft:12}}>距{data.next_solar_term}还有{data.next_solar_term_days}天</span>
      </div>

      {/* 月历视图 */}
      {month && (
        <div style={card}>
          <div style={{fontSize:14,fontWeight:600,color:GD,marginBottom:8}}>
            {month.year}年{month.month}月
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
            {["一","二","三","四","五","六","日"].map(w => (
              <div key={w} style={{textAlign:"center",fontSize:10,color:"#9CA3AF",padding:"2px 0"}}>{w}</div>
            ))}
            {Array.from({length:new Date(month.year,month.month-1,1).getDay()||7}).map((_,i) =>
              <div key={"e"+i} style={{height:40}} />
            )}
            {month.days.map(d => {
              const isToday = new Date().getDate() === d.day && new Date().getMonth()+1 === month.month;
              const isGood = d.jianchu === "成" || d.jianchu === "开";
              const isBad = d.jianchu === "破" || d.jianchu === "闭";
              return (
                <div key={d.day} style={{
                  textAlign:"center",padding:3,borderRadius:6,fontSize:11,
                  background: isToday ? "#FEF3C7" : isGood ? "#ECFDF5" : isBad ? "#FEF2F2" : "transparent",
                  color: isBad ? "#DC2626" : isGood ? "#059669" : "#4B5563",
                  border: isToday ? `1px solid ${GDH}` : "1px solid transparent",
                }}>
                  <div style={{fontWeight:isToday?700:400}}>{d.day}</div>
                  <div style={{fontSize:8}}>{d.jianchu}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 解梦 Tab
// ═══════════════════════════════════════════════════════════════════════════════

const SCENES = ["","求职期","孕期","少儿期","恋爱期","创业期"];

function DreamsTab() {
  const [keywords, setKeywords] = useState([]);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [scene, setScene] = useState("");

  useEffect(() => {
    fetch(`${API}/tools/dreams`).then(r => r.json())
      .then(d => setKeywords(d.keywords||[]));
  }, []);

  const search = async () => {
    if (!query.trim()) return;
    const sceneParam = scene ? `&scene=${encodeURIComponent(scene)}` : "";
    const r = await fetch(`${API}/tools/dreams?keyword=${encodeURIComponent(query)}${sceneParam}`);
    setResult(await r.json());
  };

  const fc = (f) => {
    if (f === "大吉") return "#059669";
    if (f === "吉" || f === "上吉" || f === "中吉") return GDH;
    if (f === "凶" || f === "下下") return "#DC2626";
    return "#6B7280";
  };

  return (
    <div>
      {/* 场景选择器 */}
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {SCENES.map(s => (
          <button key={s||"all"} onClick={()=>setScene(s||"")} style={{
            padding:"4px 12px",borderRadius:15,border:`1px solid ${scene===s?GDH:"#E5E7EB"}`,
            background:scene===s?"#FEF3C7":"#FFF",color:scene===s?GD:"#6B7280",
            cursor:"pointer",fontSize:12,
          }}>{s||"通用"}</button>
        ))}
      </div>

      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <input value={query} onChange={e=>setQuery(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&search()}
          placeholder="输入梦境关键词或描述，如: 蛇、水、考试..."
          style={{flex:1,padding:"10px 16px",border:"1px solid #D1D5DB",borderRadius:10,fontSize:14,outline:"none"}} />
        <button onClick={search} style={{
          padding:"10px 24px",background:GD,color:"#FFF",border:"none",borderRadius:10,
          fontSize:14,cursor:"pointer",fontWeight:600,
        }}>🔍 解梦</button>
      </div>

      {result ? (
        result.interpretations ? (
          // 多关键词结果
          <div>
            <div style={{fontSize:12,color:"#6B7280",marginBottom:8}}>
              检测到 {result.count} 个意象: {result.keywords?.join(" · ")}
              {result.scene && <span> · 场景: {result.scene}</span>}
            </div>
            {result.interpretations.map((ir,i) => (
              <div key={i} style={{...card,marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <h3 style={{fontSize:16,color:GD,margin:0}}>{ir.keyword}</h3>
                  <span style={{fontSize:12,fontWeight:700,color:fc(ir.fortune)}}>{ir.fortune}</span>
                </div>
                <p style={{fontSize:12,color:"#9CA3AF",marginBottom:4,fontStyle:"italic"}}>📖 {ir.classical}</p>
                <p style={{fontSize:13,color:"#4B5563",lineHeight:1.7}}>{ir.modern}</p>
                {ir.scene_variation && (
                  <p style={{fontSize:13,color:GD,lineHeight:1.7,marginTop:8,paddingTop:8,borderTop:"1px solid #F3F4F6"}}>
                    🎯 {ir.scene_variation}
                  </p>
                )}
              </div>
            ))}
            {/* LLM 梦境综合叙事 */}
            {result.synthesis && (
              <div style={{...card,marginTop:12,background:"linear-gradient(135deg,#F0FDF4,#ECFDF5)",border:"1px solid rgba(6,78,59,0.15)"}}>
                <div style={{fontSize:12,color:GD,fontWeight:600,marginBottom:6}}>🧠 梦境综合解读</div>
                <p style={{fontSize:13,color:"#1F2937",lineHeight:1.9,margin:0}}>{result.synthesis}</p>
              </div>
            )}
          </div>
        ) : result.found === false ? (
          <div style={card}>
            <p style={{color:"#6B7280"}}>未找到"{result.keyword}"</p>
            {result.suggestions?.length > 0 && (
              <div>
                <p style={{fontSize:12,color:"#6B7280",marginTop:8}}>相关梦境:</p>
                {result.suggestions.map((s,i) => (
                  <span key={i} onClick={()=>{setQuery(s);search();}} style={{
                    display:"inline-block",background:"#F3F4F6",padding:"4px 12px",
                    borderRadius:15,fontSize:12,margin:"4px 4px",cursor:"pointer",color:GD,
                  }}>{s}</span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <h3 style={{fontSize:18,color:GD,margin:0}}>{result.keyword}</h3>
              <span style={{fontSize:14,fontWeight:700,color:fc(result.fortune)}}>
                {result.fortune} {result.fortune==="大吉"?"🌟":result.fortune==="凶"?"⚠️":""}
              </span>
            </div>
            <p style={{fontSize:12,color:"#9CA3AF",marginBottom:8,fontStyle:"italic"}}>📖 {result.classical}</p>
            <p style={{fontSize:14,color:"#4B5563",lineHeight:1.8}}>{result.modern}</p>
            {result.scene_variation && (
              <p style={{fontSize:14,color:GD,lineHeight:1.8,marginTop:10,paddingTop:10,borderTop:"1px solid #F3F4F6"}}>
                🎯 当前场景({result.scene}): {result.scene_variation}
              </p>
            )}
          </div>
        )
      ) : (
        <div style={card}>
          <p style={{fontSize:12,color:"#9CA3AF",marginBottom:8}}>常见梦境关键词 (点击查询):</p>
          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
            {(keywords||[]).slice(0,50).map(k => (
              <span key={k} onClick={()=>{setQuery(k);search();}} style={{
                background:"#F3F4F6",padding:"4px 12px",borderRadius:15,fontSize:12,
                cursor:"pointer",color:"#4B5563",border:"1px solid #E5E7EB",
              }}>{k}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 观音灵签 Tab
// ═══════════════════════════════════════════════════════════════════════════════

function DivinationTab() {
  const [result, setResult] = useState(null);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);

  const draw = async () => {
    setLoading(true);
    const q = question.trim() ? `&question=${encodeURIComponent(question)}` : "";
    const r = await fetch(`${API}/tools/divination?${q}`);
    setResult(await r.json());
    setLoading(false);
  };

  const fc = (f) => {
    if (f === "大吉") return "#059669";
    if (f.includes("吉")) return GDH;
    if (f === "下下") return "#DC2626";
    return "#6B7280";
  };

  return (
    <div>
      <div style={{...card,textAlign:"center",marginBottom:16}}>
        <p style={{fontSize:14,color:"#6B7280",marginBottom:12}}>
          心中默念问题，然后抽签
        </p>
        <input value={question} onChange={e=>setQuestion(e.target.value)}
          placeholder="（可选）写下你想问的事..."
          style={{padding:"10px 16px",border:"1px solid #D1D5DB",borderRadius:10,
            fontSize:14,width:"100%",maxWidth:400,marginBottom:12,outline:"none",
            display:"block",margin:"0 auto 12px"}} />
        <button onClick={draw} disabled={loading} style={{
          padding:"16px 48px",background:loading?"#9CA3AF":"#DC2626",color:"#FFF",
          border:"none",borderRadius:16,fontSize:18,cursor:loading?"wait":"pointer",
          fontWeight:700,
        }}>
          🏮 {loading ? "摇签中..." : "抽 签"}
        </button>
      </div>

      {result && (
        <div style={{...card,textAlign:"center"}}>
          {/* 签号 */}
          <div style={{
            width:80,height:80,borderRadius:"50%",
            background: result.fortune === "大吉" ? "#ECFDF5" :
                        result.fortune === "下下" ? "#FEF2F2" : "#FEF3C7",
            display:"flex",alignItems:"center",justifyContent:"center",
            margin:"0 auto 12px",border:`3px solid ${fc(result.fortune)}`,
          }}>
            <span style={{fontSize:32,fontWeight:700,color:fc(result.fortune)}}>
              {result.lot_num}
            </span>
          </div>
          <div style={{fontSize:20,fontWeight:700,color:fc(result.fortune),marginBottom:16}}>
            第{result.lot_num}签 · {result.fortune}
          </div>

          {/* 签诗 */}
          <div style={{
            background:"#F9FAFB",padding:"14px",borderRadius:8,
            fontSize:14,color:"#4B5563",lineHeight:2,fontStyle:"italic",
            marginBottom:12,
          }}>
            {result.poem}
          </div>

          {/* 诗意 */}
          {result.poetry_meaning && (
            <div style={{background:"#FFFDF5",padding:"12px",borderRadius:8,marginBottom:10,textAlign:"left"}}>
              <div style={{fontSize:11,color:GDH,fontWeight:600,marginBottom:4}}>📖 诗意</div>
              <p style={{fontSize:13,color:"#4B5563",lineHeight:1.7,margin:0}}>{result.poetry_meaning}</p>
            </div>
          )}

          {/* 解曰 */}
          {result.explanation && (
            <div style={{background:"#F3F4F6",padding:"12px",borderRadius:8,marginBottom:10,textAlign:"left"}}>
              <div style={{fontSize:11,color:"#6B7280",fontWeight:600,marginBottom:4}}>📋 解曰</div>
              <p style={{fontSize:12,color:"#4B5563",lineHeight:1.8,margin:0,whiteSpace:"pre-wrap"}}>{result.explanation.split(" | ").join("\n")}</p>
            </div>
          )}

          {/* 仙机故事 */}
          {result.story && (
            <div style={{background:"#F0FDF4",padding:"12px",borderRadius:8,marginBottom:12,textAlign:"left"}}>
              <div style={{fontSize:11,color:"#059669",fontWeight:600,marginBottom:4}}>🏯 仙机</div>
              <p style={{fontSize:12,color:"#4B5563",lineHeight:1.7,margin:0}}>{result.story}</p>
            </div>
          )}

          {/* LLM 现代白话翻译 */}
          {result.modern_translation && (
            <div style={{background:"#FFFDF5",border:"1px solid rgba(212,175,55,0.3)",padding:"14px",borderRadius:10,marginBottom:12,textAlign:"left"}}>
              <div style={{fontSize:11,color:GDH,fontWeight:600,marginBottom:6}}>💬 现代解读</div>
              <p style={{fontSize:13,color:"#4B5563",lineHeight:1.9,margin:0}}>{result.modern_translation}</p>
            </div>
          )}

          {/* 白话解读 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,textAlign:"left",marginTop:8}}>
            <div><span style={{fontSize:11,color:"#9CA3AF"}}>事业:</span><br/><span style={{fontSize:12}}>{result.career}</span></div>
            <div><span style={{fontSize:11,color:"#9CA3AF"}}>感情:</span><br/><span style={{fontSize:12}}>{result.love}</span></div>
            <div><span style={{fontSize:11,color:"#9CA3AF"}}>健康:</span><br/><span style={{fontSize:12}}>{result.health}</span></div>
          </div>

          {/* 下下签保护文案 */}
          {result.is_ominous && result.protection && (
            <div style={{
              background:"#FFF5F5",border:"1px solid #FECACA",borderRadius:12,
              padding:"16px",marginTop:16,textAlign:"left",
            }}>
              <div style={{fontSize:13,color:"#991B1B",fontWeight:700,marginBottom:8}}>🛡️ 心灵护盾</div>
              <p style={{fontSize:13,color:"#7F1D1D",lineHeight:1.8,margin:0}}>{result.protection}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AI起名 Tab
// ═══════════════════════════════════════════════════════════════════════════════

function NamingTab() {
  const [surname, setSurname] = useState("");
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useBazi, setUseBazi] = useState(false);
  const [birthY, setBirthY] = useState("");
  const [birthM, setBirthM] = useState("");
  const [birthD, setBirthD] = useState("");
  const [birthH, setBirthH] = useState("12");
  const [gender, setGender] = useState("N");
  const [xiyong, setXiyong] = useState(null);

  const generate = async () => {
    if (!surname.trim()) return;
    setLoading(true);
    const formData = new URLSearchParams();
    formData.append("surname", surname);
    formData.append("count", "12");
    formData.append("gender", gender);
    if (useBazi && birthY && birthM && birthD) {
      formData.append("birth_year", birthY);
      formData.append("birth_month", birthM);
      formData.append("birth_day", birthD);
      formData.append("birth_hour", birthH);
    }
    const r = await fetch(`${API}/tools/naming`, {
      method:"POST",
      headers:{"Content-Type":"application/x-www-form-urlencoded"},
      body: formData.toString(),
    });
    const d = await r.json();
    setNames(d.names||[]);
    setXiyong(d.xiyong||null);
    setLoading(false);
  };

  const ec = (e) => {
    const m = {"木":"#059669","火":"#DC2626","土":"#D97706","金":GDH,"水":"#2563EB"};
    return m[e]||"#6B7280";
  };

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <input value={surname} onChange={e=>setSurname(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&generate()}
          placeholder="输入姓氏..."
          style={{flex:1,padding:"10px 16px",border:"1px solid #D1D5DB",borderRadius:10,fontSize:14,outline:"none"}} />
        <button onClick={generate} disabled={loading} style={{
          padding:"10px 24px",background:GD,color:"#FFF",border:"none",borderRadius:10,
          fontSize:14,cursor:"pointer",fontWeight:600,
        }}>
          {loading ? "生成中..." : "✨ 起名"}
        </button>
      </div>

      {/* 八字接入开关 */}
      <div style={{marginBottom:12}}>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:"#6B7280"}}>
          <input type="checkbox" checked={useBazi} onChange={e=>setUseBazi(e.target.checked)}
            style={{width:16,height:16,accentColor:GD}} />
          输入生辰八字，精准补缺五行
        </label>
        {useBazi && (
          <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap",alignItems:"center"}}>
            <input value={birthY} onChange={e=>setBirthY(e.target.value)} placeholder="年" style={bInput} />
            <input value={birthM} onChange={e=>setBirthM(e.target.value)} placeholder="月" style={bInput} />
            <input value={birthD} onChange={e=>setBirthD(e.target.value)} placeholder="日" style={bInput} />
            <input value={birthH} onChange={e=>setBirthH(e.target.value)} placeholder="时" style={bInput} />
            <select value={gender} onChange={e=>setGender(e.target.value)} style={{...bInput,width:60}}>
              <option value="N">不限</option><option value="M">男</option><option value="F">女</option>
            </select>
          </div>
        )}
      </div>

      {/* 喜用神信息 */}
      {xiyong && !xiyong.error && (
        <div style={{...card,marginBottom:12,background:"#FFFDF5",textAlign:"center"}}>
          <span style={{fontSize:12,color:"#6B7280"}}>日主</span>
          <span style={{fontSize:14,fontWeight:700,color:GD,margin:"0 8px"}}>{xiyong.daymaster}</span>
          <span style={{fontSize:12,color:"#6B7280"}}>· {xiyong.strength} · 用神</span>
          <span style={{fontSize:14,fontWeight:700,color:"#059669",margin:"0 4px"}}>{xiyong.yong_shen}</span>
          <span style={{fontSize:12,color:"#6B7280"}}>喜神</span>
          <span style={{fontSize:14,fontWeight:700,color:GDH}}>{xiyong.xi_shen}</span>
        </div>
      )}

      {names.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {names.map((n,i) => (
            <div key={i} style={{...card,textAlign:"center"}}>
              <div style={{
                fontSize:22,fontWeight:700,color:GD,marginBottom:4,
                fontFamily:"'Noto Serif SC',serif",
              }}>{n.name}</div>
              <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                <span style={{...badge("#F3F4F6",ec(n.mid_element))}}>
                  {n.mid_char} {n.mid_element} {n.mid_stroke}画
                </span>
                <span style={{...badge("#F3F4F6",ec(n.tail_element))}}>
                  {n.tail_char} {n.tail_element} {n.tail_stroke}画
                </span>
              </div>
              <div style={{fontSize:10,color:"#9CA3AF",marginBottom:4}}>
                总{n.total_strokes}画 · 五行补缺{n.deficiency_score}分
              </div>
              {n.wuge && (
                <div style={{display:"flex",justifyContent:"center",gap:4,marginBottom:4,flexWrap:"wrap"}}>
                  {["tian","ren","di","zong","wai"].map(k => {
                    const w = n.wuge[k];
                    const labels = {tian:"天",ren:"人",di:"地",zong:"总",wai:"外"};
                    return (
                      <span key={k} style={{
                        fontSize:9,padding:"1px 5px",borderRadius:6,
                        background: w.is_ji ? "#ECFDF5" : "#FEF2F2",
                        color: w.is_ji ? "#065F46" : "#991B1B",
                        border: `1px solid ${w.is_ji ? "#A7F3D0" : "#FECACA"}`,
                      }} title={`${labels[k]}格${w.num}画: ${w.ji}`}>
                        {labels[k]}{w.num}{w.is_ji?"✓":"✗"}
                      </span>
                    );
                  })}
                </div>
              )}
              <p style={{fontSize:11,color:"#6B7280",margin:0,lineHeight:1.5}}>{n.meaning}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 主组件
// ═══════════════════════════════════════════════════════════════════════════════

function badge(bg, color) {
  return {background:bg,color:color,padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600};
}

const bInput = {width:50,padding:"6px 8px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,outline:"none",textAlign:"center"};

export default function ToolsPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "almanac");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{maxWidth:760,margin:"0 auto",padding:"40px 20px 80px",background:BG,minHeight:"100vh"}}>
      <h1 style={{fontSize:24,color:GD,textAlign:"center",marginBottom:4}}>易学工具箱</h1>
      <p style={{textAlign:"center",color:"#6B7280",fontSize:13,marginBottom:24}}>
        老黄历 · 解梦 · 求签 · 起名 — 全部免费 · 无需登录
      </p>

      <div style={{display:"flex",gap:6,marginBottom:24,flexWrap:"wrap",justifyContent:"center"}}>
        {tabs.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)} style={{
            padding:"8px 16px",borderRadius:20,
            border: `2px solid ${tab===t.key?GDH:"#E5E7EB"}`,
            background: tab===t.key?"#FEF3C7":CARD_BG,
            color: tab===t.key?GD:"#6B7280",
            cursor:"pointer",fontSize:13,fontWeight:tab===t.key?600:400,
          }} title={t.desc}>{t.label}</button>
        ))}
      </div>

      {tab === "almanac" && <AlmanacTab />}
      {tab === "dreams" && <DreamsTab />}
      {tab === "divination" && <DivinationTab />}
      {tab === "naming" && <NamingTab />}

      {/* 命盘引流条 */}
      <div style={{
        marginTop: 32, padding: "20px 24px",
        background: "linear-gradient(135deg,rgba(212,175,55,.06),rgba(6,78,59,.04))",
        borderRadius: 14,
        border: "1px solid rgba(212,175,55,.22)",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#8B6914", marginBottom: 6 }}>
          🔮 想了解你自己的命盘吗？
        </div>
        <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, maxWidth: 480, margin: "0 auto 14px" }}>
          八字+紫微双引擎深度解读——日主强弱、十神性格、大运流年、十二宫全分析。引擎真算，不是AI凭空生成。
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
          <a href="/audit" style={{
            display: "inline-block", padding: "10px 24px",
            background: "linear-gradient(135deg,#D4AF37,#064E3B)",
            color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 600,
            textDecoration: "none", letterSpacing: ".03em",
          }}>
            免费试排 →
          </a>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>无需信用卡 · 2分钟出结果</span>
        </div>
      </div>
    </div>
  );
}
